<script setup lang="ts">
import type { FrontendUserAvatarIdentityVerificationItem } from '@/api/modules/frontendUser'
import type { ApiRequestError } from '@/api/requestError'
import type { SchemaFormExpose, SchemaFormItem, SchemaFormOptions } from '@/components/FaSchemaForm/types'
import apiFrontendUser from '@/api/modules/frontendUser'
import { normalizeApiRequestError } from '@/api/requestError'
import AppRequestState from '@/components/AppDisplay/RequestState.vue'
import FaSchemaForm from '@/components/FaSchemaForm/index.vue'

interface FormState {
  reason: string
}

defineOptions({ name: 'AvatarIdentityInvalidateDialog' })

const props = defineProps<{
  userId: number
  record: FrontendUserAvatarIdentityVerificationItem | null
}>()
const emit = defineEmits<{ saved: [] }>()
const visible = defineModel<boolean>({ required: true })
const formRef = useTemplateRef<SchemaFormExpose<FormState>>('formRef')
const submitting = shallowRef(false)
const submitError = shallowRef<ApiRequestError | null>(null)
const form = reactive<FormState>({ reason: '' })
const formOptions: SchemaFormOptions = { labelWidth: 90, cols: 1 }
const formItems: SchemaFormItem<FormState>[] = [{
  label: '撤销原因',
  prop: 'reason',
  render: 'input',
  renderProps: {
    type: 'textarea',
    rows: 5,
    maxlength: 255,
    showWordLimit: true,
    placeholder: '请填写审核依据或头像认证失效原因',
  },
  rules: [{ required: true, message: '请输入撤销原因' }],
}]

watch(
  [visible, () => props.record],
  ([isVisible]) => {
    if (!isVisible) {
      return
    }
    form.reason = ''
    submitError.value = null
    nextTick(() => formRef.value?.clearValidate())
  },
)

async function submit() {
  if (!props.record || !(await formRef.value?.validate())) {
    return false
  }
  const reason = form.reason.trim()
  if (!reason) {
    return false
  }
  submitting.value = true
  submitError.value = null
  try {
    await apiFrontendUser.invalidateAvatarIdentity(props.userId, props.record.id, reason)
  }
  catch (reason) {
    submitError.value = normalizeApiRequestError(reason)
    return false
  }
  finally {
    submitting.value = false
  }
  emit('saved')
  useFaToast().success('真人头像认证已撤销')
  return true
}

async function handleBeforeClose(action: string, done: () => void) {
  if (action !== 'confirm' || await submit()) {
    done()
  }
}
</script>

<template>
  <FaModal
    v-model="visible"
    title="撤销真人头像认证"
    description="撤销后该头像不再展示真人标识；管理员不能手工将认证改为通过。"
    destroy-on-close
    class="sm:max-w-[620px]"
    show-cancel-button
    :close-on-click-overlay="false"
    :confirm-button-loading="submitting"
    :before-close="handleBeforeClose"
    confirm-button-text="确认撤销"
  >
    <div class="space-y-4">
      <AppRequestState v-if="submitError" compact kind="error" title="真人头像认证撤销失败" :description="submitError.message" :request-id="submitError.requestId" />
      <FaSchemaForm ref="formRef" v-model="form" :items="formItems" :options="formOptions" />
    </div>
  </FaModal>
</template>
